import { useEffect, useState } from "react";
import Express from "@Meridian-Developers/meridian-ui/middleware_controller";
import StateTree from "../../globalHelpers/StateTree";
import {
    sessionVariableMatch,
    createSessionProcesses,
} from "../../globalHelpers/SessionUIHelper";
import { WidthProvider, Responsive } from "react-grid-layout";
import WidgetViewer from "./WidgetViewer";
import _, { map } from "lodash";
import Spinner from "../Components/Spinner";
import T2 from "./T2";

import "/node_modules/react-grid-layout/css/styles.css";
import "/node_modules/react-resizable/css/styles.css";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

let pageCache = null;
let accountCache = {};

function PageViewerV2(props) {
    const [_layoutOn, setLayoutOn] = useState(null);
    const [_layouts, setLayouts] = useState(null);
    const [_fullLayouts, setFullLayouts] = useState(null);
    const [_loading, setLoading] = useState(true);
    const [_pageError, setPageError] = useState(false);
    const [_accountContext, setAccountContext] = useState(null);
    const [_pageNameCache, setPageNameCache] = useState({});
    const [_layoutChanges, setLayoutChanges] = useState([]);

    useEffect(() => {
        const path = window.location.pathname;
        fetchPageLayouts(path);
        createSessionProcesses();

        const subscription = StateTree.getDataPool().subscribe((dataPool) => {
            if (
                !dataPool ||
                !dataPool.accountContext ||
                accountCache[dataPool.accountContext.accountId]
            ) {
                return;
            }

            setAccountContext(dataPool.accountContext);

            if (pageCache) {
                accountCache = { [dataPool.accountContext.accountId]: true };
            }

            generateDynamicLayout(pageCache, dataPool.accountContext);
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    async function fetchPageLayouts(path) {
        try {
            setLoading(true);

            if (!path) {
                console.log("Missing path");
                return;
            }

            const page = await Express.callPublic("fetch-page", {
                route: path,
            });

            if (!page || !page.pageName) {
                console.log("Missing Page");
                setPageError(true);
                return;
            }

            const pageName = page.pageName;

            if (page.dynamicLayouts) {
                pageCache = page;
                generateDynamicLayout(page, _accountContext);
                return;
            }

            loadPageLaout(pageName);
        } catch (e) {
            console.log(e);
        }
    }

    async function loadPageLaout(pageName) {
        try {
            let pageLayout = _pageNameCache[pageName];

            if (!pageLayout) {
                pageLayout = await Express.callPublic("fetch-page-layout", {
                    pageName,
                });
            }

            if (!pageLayout || !pageLayout.layout) {
                console.log("Missing or invalid page layout");
                setPageError(true);
                return;
            }

            setPageNameCache({ ..._pageNameCache, [pageName]: pageLayout });

            await generateLayouts(pageLayout.layout);

            setLoading(false);
        } catch (e) {
            console.log(e);
        }
    }

    function generateDynamicLayout(page, accountContext) {
        try {
            if (!page || !page.dynamicLayouts || !accountContext) return;

            setLoading(true);

            const dynamicLayouts = page.dynamicLayouts;

            for (let i = 0; i < dynamicLayouts.length; i++) {
                const layout = dynamicLayouts[i];
                const { key, value, match, pageName } = layout;

                const isMatch = sessionVariableMatch(
                    accountContext,
                    key,
                    value,
                    match
                );

                if (!isMatch) continue;

                loadPageLaout(pageName);
                return;
            }

            loadPageLaout(page.pageName);
        } catch (e) {
            console.log(e);
        }
    }

    async function generateLayouts(layout) {
        try {
            // In the agent portal there is only a desktop layout
            // For more complex systems. Additional consideration will need to be made
            // When determining breakpoints and widget imports.
            if (!layout) return;

            const desktop = createLayoutExclusions(layout.desktop);
            const tablet = createLayoutExclusions(layout.tablet);
            const cell = createLayoutExclusions(layout.cell);

            const results = {
                lg: desktop.gridLayout,
                md: tablet.gridLayout,
                sm: tablet.gridLayout,
                xs: cell.gridLayout,
                xss: cell.gridLayout,
            };

            setFullLayouts({
                lg: desktop,
                md: tablet,
                sm: cell,
                xs: cell,
                xss: cell,
            });

            setLayouts(results);
            setLayoutOn(desktop);

            return;
        } catch (e) {
            console.log(e);
        }
    }

    function createLayoutExclusions(layout) {
        try {
            if (!layout) return {};

            const top = [];
            const bottom = [];
            const right = [];
            const left = [];
            const gridLayout = [];

            const excludeMap = {
                TOP: top,
                BOTTOM: bottom,
                RIGHT: right,
                LEFT: left,
            };

            for (let i = 0; i < layout.length; i++) {
                const grid = layout[i];

                if (typeof grid["i"] === typeof {}) {
                    const name = Object.keys(grid["i"])[0];

                    grid["nested"] = { layout: grid["i"], name };
                    grid["i"] = name;
                }

                const dataGrid = { ...grid["data-grid"], ...grid };

                if (grid.overridePosition) {
                    excludeMap[grid.overridePosition].push(dataGrid);
                } else {
                    gridLayout.push(dataGrid);
                }
            }

            return {
                top,
                bottom,
                right,
                left,
                gridLayout,
            };
        } catch (e) {
            console.log(e);
            return {};
        }
    }

    function changeBreakpoint(breakpoint) {
        try {
            const breakpointLayout = _fullLayouts[breakpoint];

            if (!breakpointLayout) return;

            const { top, bottom, gridLayout } = breakpointLayout;

            if (
                top.length === 0 &&
                bottom.length === 0 &&
                gridLayout.length === 0
            ) {
                return;
            }

            setLayoutOn(_fullLayouts[breakpoint]);
        } catch (e) {
            console.log(e);
        }
    }

    function renderTopLayout() {
        try {
            if (!_layoutOn || !_layoutOn.top) return;

            return map(_layoutOn.top, (layout) => {
                return (
                    <div key={layout.i} style={{ ...layout.overrideStyle }}>
                        {renderWidget(layout)}
                    </div>
                );
            });
        } catch (e) {
            console.log(e);
        }
    }

    function renderBottomLayout() {
        try {
            if (!_layoutOn || !_layoutOn.bottom) return;

            return map(_layoutOn.bottom, (layout) => {
                return (
                    <div key={layout.i} style={{ ...layout.overrideStyle }}>
                        {renderWidget(layout)}
                    </div>
                );
            });
        } catch (e) {
            console.log(e);
        }
    }

    function renderPosition(index) {
        try {
            const layout = _layoutChanges[index];

            if (!layout) return;

            const position = JSON.stringify(layout);

            return (
                <div
                    style={{
                        position: "absolute",
                        marginTop: "-1em",
                        cursor: "pointer",
                    }}
                    onClick={() => {
                        navigator.clipboard.writeText(position);
                    }}
                >
                    {position}
                </div>
            );
        } catch (e) {
            console.log(e);
        }
    }

    function renderLayouts() {
        try {
            if (
                !_layoutOn ||
                !_layoutOn.gridLayout ||
                !(_layoutOn.gridLayout.length > 0)
            ) {
                return;
            }

            return (
                <div id="idea-grid-layout">
                    <ResponsiveReactGridLayout
                        className="idea-grid-layout"
                        layouts={_layouts}
                        breakpoints={{
                            lg: 1200,
                            md: 996,
                            sm: 768,
                            xs: 480,
                            xss: 0,
                        }}
                        autoSize={true}
                        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xss: 2 }}
                        containerPadding={[0, 0]}
                        rowHeight={10}
                        onBreakpointChange={changeBreakpoint}
                        onLayoutChange={setLayoutChanges}
                    >
                        {map(_layoutOn.gridLayout, (layout, index) => {
                            return (
                                <div
                                    key={layout.i}
                                    data-grid={layout["data-grid"]}
                                    className="idea-layout-item-container"
                                    style={{
                                        ...layout.overrideStyle,
                                    }}
                                >
                                    {!layout.static && renderPosition(index)}
                                    {renderWidget(layout)}
                                </div>
                            );
                        })}
                    </ResponsiveReactGridLayout>
                </div>
            );
        } catch (e) {
            console.log(e);
        }
    }

    function renderWidget(layout) {
        try {
            if (!layout) return;

            let nestedWidgetName = null;
            let nestedWidget = null;

            if (layout.nested) {
                nestedWidgetName = layout.nested.name;
                nestedWidget = { ...layout, i: layout.nested.layout };
            }

            return (
                <WidgetViewer
                    widgetName={nestedWidgetName || layout.i}
                    widget={nestedWidget || layout}
                />
            );
        } catch (e) {
            console.log(e);
            return <div />;
        }
    }

    if (_pageError) {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1em",
                    textAlign: "center",
                }}
            >
                <div>Invalid Page</div>
                <div>
                    To return home{" "}
                    <button
                        onClick={() => {
                            window.location.href = "/";
                        }}
                    >
                        Click Here
                    </button>
                </div>
            </div>
        );
    }

    if (_loading) {
        return (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                }}
            >
                <Spinner width="50px" />
                <br />
                <span>
                    <T2 id="1141">Loading customer data...</T2>
                </span>
            </div>
        );
    }

    return (
        <div>
            {renderTopLayout()}
            {renderLayouts()}
            {renderBottomLayout()}
        </div>
    );
}

export default PageViewerV2;
